import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/modules/collector_v2/lib/api";

const POLL_INTERVAL_MS = 2000;
const TERMINAL_STATES = new Set([
  "completed",
  "failed",
  "expired",
  "cancelled",
]);
const ADMIN_TOKEN_STORAGE_KEY = "ppk_admin_token";

const getAdminToken = () => localStorage.getItem(ADMIN_TOKEN_STORAGE_KEY) || "";
const getJwt = () =>
  localStorage.getItem("token") || localStorage.getItem("access_token") || "";

/**
 * Intenta recuperar el admin token automáticamente vía el endpoint puente
 * /api/pppoker/v2/admin-token-bridge, usando el JWT del usuario admin logueado.
 * Si tiene éxito, lo guarda en localStorage y devuelve el token.
 */
async function autoFetchAdminToken() {
  const jwt = getJwt();
  if (!jwt) return null;
  try {
    const { data } = await axios.get(
      `${process.env.REACT_APP_BACKEND_URL || ""}/api/pppoker/v2/admin-token-bridge`,
      {
        headers: { Authorization: `Bearer ${jwt}` },
        timeout: 6000,
      }
    );
    const tok = data?.admin_token;
    if (tok) {
      try {
        localStorage.setItem(ADMIN_TOKEN_STORAGE_KEY, tok);
      } catch {
        /* no bloqueante */
      }
      return tok;
    }
  } catch {
    /* silencioso — caer al fallback de input manual */
  }
  return null;
}

/**
 * Hook para el Wizard de captura RDKEY desde Mac.
 *
 * Comparte estructura con useRdkeyCaptureJob.js (Wizard de VM) pero apunta
 * al backend `/v2/collector/mac-agent/capture/*` que usa Mac Agent v3
 * con polling on-demand.
 */
export function useMacAgentCaptureJob() {
  const [job, setJob] = useState(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [agentOnline, setAgentOnline] = useState(false);
  const [agentLastSeenS, setAgentLastSeenS] = useState(null);
  const [agentHostname, setAgentHostname] = useState(null);
  const intervalRef = useRef(null);
  const jobIdRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const fetchStatus = useCallback(
    async (jobId) => {
      const token = getAdminToken();
      try {
        const { data } = await axios.get(
          `${API}/v2/collector/mac-agent/capture/status/${jobId}`,
          { headers: { "X-Admin-Token": token }, timeout: 4000 }
        );
        setJob(data);
        setAgentOnline(!!data?.agent_online);
        setAgentLastSeenS(data?.agent_last_seen_s ?? null);
        setAgentHostname(data?.agent_hostname_hb || data?.agent_hostname || null);
        if (data && TERMINAL_STATES.has(data.state)) {
          stopPolling();
        }
        return data;
      } catch (e) {
        setError(
          e?.response?.data?.detail?.message ||
            e?.response?.data?.detail ||
            e.message
        );
        return null;
      }
    },
    [stopPolling]
  );

  const fetchActive = useCallback(async () => {
    let token = getAdminToken();
    // ⭐ Si no hay admin token, intentar obtenerlo automáticamente vía bridge JWT
    if (!token) {
      token = await autoFetchAdminToken();
    }
    if (!token) {
      setError(
        "No se pudo obtener admin token automáticamente. Verificá que estés logueado como admin."
      );
      setAgentOnline(false);
      return null;
    }
    try {
      const { data } = await axios.get(
        `${API}/v2/collector/mac-agent/capture/active`,
        { headers: { "X-Admin-Token": token }, timeout: 4000 }
      );
      setAgentOnline(!!data?.agent_online);
      setAgentLastSeenS(data?.agent_last_seen_s ?? null);
      setAgentHostname(data?.agent_hostname || null);
      if (data) setError(null);
      return data;
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        // token guardado quedó stale — borrarlo y reintentar
        localStorage.removeItem(ADMIN_TOKEN_STORAGE_KEY);
        setError("Admin token expirado. Refrescá la página para reintentar.");
      }
      return null;
    }
  }, []);

  const startPolling = useCallback(
    (jobId) => {
      stopPolling();
      jobIdRef.current = jobId;
      intervalRef.current = setInterval(() => {
        if (jobIdRef.current) fetchStatus(jobIdRef.current);
      }, POLL_INTERVAL_MS);
    },
    [fetchStatus, stopPolling]
  );

  const startJob = useCallback(async () => {
    setStarting(true);
    setError(null);
    let token = getAdminToken();
    if (!token) {
      token = await autoFetchAdminToken();
    }
    if (!token) {
      setError(
        "No se pudo obtener admin token. Asegurate de estar logueado como admin."
      );
      setStarting(false);
      return null;
    }
    try {
      const { data } = await axios.post(
        `${API}/v2/collector/mac-agent/capture/start`,
        { expected_uid: 13551134, bundle_id: "local.evogame.pppoker.observer02" },
        {
          headers: {
            "X-Admin-Token": token,
            "Content-Type": "application/json",
          },
          timeout: 8000,
        }
      );
      setJob(data);
      startPolling(data.job_id);
      setStarting(false);
      return data;
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        (typeof detail === "object" && (detail?.message || detail?.error)) ||
        detail ||
        e.message;
      setError(msg);
      setStarting(false);
      return null;
    }
  }, [startPolling]);

  const cancelJob = useCallback(async () => {
    if (!jobIdRef.current) return;
    const token = getAdminToken();
    try {
      await axios.post(
        `${API}/v2/collector/mac-agent/capture/cancel/${jobIdRef.current}`,
        {},
        { headers: { "X-Admin-Token": token }, timeout: 5000 }
      );
      await fetchStatus(jobIdRef.current);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  }, [fetchStatus]);

  const reset = useCallback(() => {
    stopPolling();
    jobIdRef.current = null;
    setJob(null);
    setError(null);
    setStarting(false);
  }, [stopPolling]);

  // Cleanup en unmount
  useEffect(() => stopPolling, [stopPolling]);

  return {
    job,
    error,
    starting,
    agentOnline,
    agentLastSeenS,
    agentHostname,
    startJob,
    cancelJob,
    reset,
    fetchActive,
  };
}
